import "../../styles/scss/resources.module.scss";
import { GetStaticProps } from "next";
import Layout from "../../components/layout";
import Link from "../../components/Link";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import "../../styles/scss/_events-page.module.scss";
import "../../styles/scss/blog.module.scss";
import TagHeroSection from "../../components/v2/blog/tag-hero-section";
import { getAllPostsForHome, getAllTagsWithSlug } from "../../lib/api";
import CallToActionSmall from "../../components/v2/call-to-action-sm";

export type TopicTag = { slug: string; name: string };

const DEFAULT_TOPIC_CARD_BLURB =
  "Articles and guides tagged with this topic.";

/** Per-topic card copy on /topics — keyed by WP tag slug and by tag name (lowercase). */
const TOPIC_CARD_BLURBS: Record<string, string> = {
  observability:
    "Learn observability concepts, best practices, trends, and real-world use cases.",
  monitoring:
    "Understand monitoring types, use cases, and techniques across systems.",
  opentelemetry:
    "Learn OpenTelemetry concepts, components, and implementation approaches",
  "open-telemetry.":
    "Learn OpenTelemetry concepts, components, and implementation approaches.",
  logging:
    "Understand logging methods, formats, patterns, and debugging approaches.",
  devops:
    "Learn DevOps concepts, workflows, key metrics, and CI/CD best practices frameworks",
  kubernetes:
    "Learn Kubernetes monitoring, troubleshooting, errors, and core concepts.",
  cloud:
    "Explore cloud strategies, migration approaches, cost optimization, and monitoring solutions.",
  containers:
    "Understand containerization, orchestration, and microservices architectures.",
  performance:
    "Learn performance optimization, latency reduction, and monitoring techniques.",
  data: "Understand data processing, streaming, and system-level concepts.",
  ai: "Explore AI-driven observability, automation, and emerging use cases.",
};

function getTopicCardBlurb(slug: string, name: string): string {
  const bySlug = TOPIC_CARD_BLURBS[slug?.toLowerCase?.() ?? ""];
  if (bySlug) return bySlug;
  const byName = TOPIC_CARD_BLURBS[name?.trim?.().toLowerCase?.() ?? ""];
  if (byName) return byName;
  return DEFAULT_TOPIC_CARD_BLURB;
}

const TOPICS_META = {
  title: "Topics",
  metaTitle: "Topics | Middleware",
  metaDesc:
    "Browse all topics including cloud, DevOps, Kubernetes, monitoring, and AI to explore helpful guides, tools, and practical insights.",
  heroTitle: "DevOps, Cloud & Observability Topics Hub",
  allTopics: "All Topics",
};

export default function TopicsPage({
  topicTags,
  allPosts,
}: {
  topicTags: TopicTag[];
  allPosts: { edges: unknown[] };
}) {
  const { edges } = allPosts;

  return (
    <>
      <Layout
        isHomePage={false}
        pageTitle={TOPICS_META.title}
        ogImage={"https://middleware.io/wp-content/uploads/2024/08/middleware-observability-thumbnail.png"}
        uri="/topics/"
        metaTitle={TOPICS_META.metaTitle}
        metaDesc={TOPICS_META.metaDesc}
        metaRobotsNoindex={"index"}
        metaRobotsNofollow={"follow"}
      >
        <div className="middle-wrapper site-page blog-v2 category-page tag-page tag-main-page">
          <TagHeroSection
            tagName={TOPICS_META.heroTitle}
            tagDescription="Explore technology topics across DevOps, observability, cloud computing, Kubernetes, monitoring, logging, and AI with structured guides and best practices."
            searchInputPostDetails={edges}
            showBackToTopics={false}
            showPopularTopics={false}
          />

          <div className="container">
            <div className="top-heading">
              <h2>{TOPICS_META.allTopics}</h2>
            </div>
            <div className={"below-section"}>
              <div className={"d-flex"}>
                {topicTags.map(({ slug, name }) => (
                  <Link key={slug} href={`/topics/${slug}/`} className="read-more">
                    <div className="topic-card">
                      <h3>{name}</h3>
                      <p>{getTopicCardBlurb(slug, name)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
        <CallToActionSmall />
      </Layout>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const locale = ctx?.locale ?? "en";
  const [tagsConnection, allPosts] = await Promise.all([
    getAllTagsWithSlug(),
    getAllPostsForHome(false, locale),
  ]);

  const topicTags: TopicTag[] =
    tagsConnection?.edges?.map(({ node }) => ({
      slug: node.slug,
      name: node.name,
    })) ?? [];

  const translations = await serverSideTranslations(
    locale,
    ["common", "header", "footer"],
    nextI18NextConfig,
  );

  return {
    props: {
      ...translations,
      topicTags,
      allPosts,
      pageTitle: "DevOps, Cloud & Observability Topics and Guides",
      description:
        "Browse all topics including cloud, DevOps, Kubernetes, monitoring, and AI to explore helpful guides, tools, and practical insights.",
      ogImage: "https://middleware.io/wp-content/uploads/2024/08/middleware-observability-thumbnail.png",
      uri: "/topics/",
      metaTitle: "DevOps, Cloud & Observability Topics and Guides",
      metaDesc:
        "Browse all topics including cloud, DevOps, Kubernetes, monitoring, and AI to explore helpful guides, tools, and practical insights.",
      metaRobotsNoindex: "index",
      metaRobotsNofollow: "follow",
    },
  };
};
